/**
 * GroupRequests (iter138) — Purely lists the unified /group-bookings queue.
 *
 * The legacy /group-requests backend was retired in iter138 (410 Gone). Any
 * historical quote-flow rows now live in `group_bookings` after the iter127
 * migration, so this list just fetches the club-flow endpoint and renders
 * category chips (Solo · Group · Club). Detail pages route to
 * /group-bookings/:id — this file no longer exports a Detail component.
 */
import { useEffect, useState, useCallback } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MailPlus, Users, CalendarClock, PoundSterling } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RequestsTabs } from "@/pages/Requests";

// Iter134 — Category filter chips. Bins match the row chip derivation
// (solo = max_players 1, club = ≥7 OR broadcast OR multi-coach, else group).
function CategoryFilter({ value, onChange }) {
  const opts = [
    { k: "all",   l: "All",   color: "#A3A3A3" },
    { k: "solo",  l: "Solo",  color: "#66C2FF" },
    { k: "group", l: "Group", color: "#CCFF00" },
    { k: "club",  l: "Club",  color: "#FF4500" },
  ];
  return (
    <div className="flex gap-2 flex-wrap" data-testid="category-filter">
      {opts.map((o) => (
        <button
          key={o.k}
          type="button"
          onClick={() => onChange(o.k)}
          data-testid={`category-filter-${o.k}`}
          className="px-3 py-1.5 text-[11px] uppercase tracking-wider border transition-colors"
          style={value === o.k
            ? { background: o.color, color: "#0A0A0A", borderColor: o.color, fontWeight: 700 }
            : { borderColor: "#262626", color: "#A3A3A3" }}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function categoryOf(r) {
  const mp = Number(r.max_players || 0);
  if ((r.coach_priority_ids || []).length > 1 || r.cascade_broadcast) return "club";
  if (mp <= 1) return "solo";
  if (mp >= 7) return "club";
  return "group";
}

/* -------------------- List view (both roles) -------------------- */
export function GroupRequestsList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const ctl = useListControls(items, { name: (r) => r.title || r.requester_name || r.student_name, fields: (r) => [r.title, r.requester_name, r.student_name, r.notes], initialSort: "default" });
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("all");
  const isCoach = user?.role === "coach" || user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = isCoach ? "/group-bookings/for-coach" : "/group-bookings/mine";
      const { data } = await api.get(path);
      const rows = (data || []).sort((a, b) => {
        const aT = new Date(a.created_at || a.updated_at || 0).getTime();
        const bT = new Date(b.created_at || b.updated_at || 0).getTime();
        return bT - aT;
      });
      setItems(rows);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isCoach]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="group-requests-list">
      <RequestsTabs active="sessions" />
      <CategoryFilter value={catFilter} onChange={setCatFilter} />
      <div className="flex items-end justify-between flex-wrap gap-3 fade-up">
        <div>
          <div className="label-mono text-[#CCFF00]">SESSION REQUESTS</div>
          <h1 className="h-display text-4xl md:text-5xl tracking-tighter mt-1">
            {isCoach ? "Sessions your students want" : "Your session requests"}
          </h1>
        </div>
        {!isCoach && (
          <Link to="/request/new" className="btn-volt py-2.5 px-5 text-xs inline-flex items-center gap-2" data-testid="new-group-request-cta">
            <MailPlus className="w-4 h-4" /> New request
          </Link>
        )}
      </div>

      {loading && <div className="text-sm text-[#A3A3A3]">Loading…</div>}
      {!loading && !items.length && (
        <div className="card-surface p-8 text-center fade-up d1" data-testid="group-requests-empty">
          <CalendarClock className="w-10 h-10 mx-auto text-[#CCFF00]" strokeWidth={1.5} />
          <div className="h-display text-2xl mt-3">{isCoach ? "No group requests waiting" : "You haven’t sent any session requests yet"}</div>
          <div className="text-sm text-[#A3A3A3] max-w-md mx-auto mt-2">
            {isCoach
              ? "When a player proposes a session (venue + date + players), you’ll see it here to approve."
              : "Pick Solo, Group or Club and send a coach a request — they’ll approve and set up payment."}
          </div>
          {!isCoach && (
            <Link to="/request/new" className="btn-volt inline-flex mt-4 py-2.5 px-5 text-xs items-center gap-2" data-testid="empty-new-request-cta">
              <MailPlus className="w-4 h-4" /> Start a request
            </Link>
          )}
        </div>
      )}

      <div className="space-y-3">
        {items.length > 0 && <ListControlsBar ctl={ctl} modes={["default", "az", "za"]} placeholder="Search requests…" testid="group-requests" />}
        {ctl.list
          .filter((r) => catFilter === "all" ? true : categoryOf(r) === catFilter)
          .map((r) => {
            const mp = Number(r.max_players || 0);
            const isMultiCoach = (r.coach_priority_ids || []).length > 1 || r.cascade_broadcast;
            const category = mp <= 1
              ? { label: "SOLO", color: "#66C2FF" }
              : (mp >= 7 || isMultiCoach)
                ? { label: "CLUB", color: "#FF4500" }
                : { label: "GROUP", color: "#CCFF00" };
            const clubMeta = {
              pending_coach: { label: "Awaiting coach", color: "#FF9F1C" },
              approved_awaiting_payment: { label: "Awaiting payment", color: "#00BFFF" },
              confirmed: { label: "Confirmed", color: "#CCFF00" },
              declined: { label: "Declined", color: "#A3A3A3" },
              cancelled: { label: "Cancelled", color: "#A3A3A3" },
              missed: { label: "Missed", color: "#FF4500" },
            }[r.status] || { label: r.status, color: "#A3A3A3" };
            const when = r.start
              || (r.date_range_start && r.date_range_end
                ? `${r.date_range_start} → ${r.date_range_end}${r.preferred_time_window ? ` (${r.preferred_time_window})` : ""}`
                : "TBC");
            return (
              <Link key={`club-${r.id}`} to={`/group-bookings/${r.id}`}
                className="card-surface p-5 flex items-center gap-4 hover:border-[#FF4500] transition-colors"
                data-testid={`gr-row-club-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold">{r.venue_name}</div>
                    <span
                      data-testid={`gr-row-chip-${category.label.toLowerCase()}-${r.id}`}
                      className="text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border"
                      style={{ color: category.color, borderColor: category.color }}
                    >
                      {category.label} REQUEST
                    </span>
                    <span className="text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border" style={{ color: clubMeta.color, borderColor: clubMeta.color }}>
                      {clubMeta.label}
                    </span>
                  </div>
                  <div className="text-xs text-[#A3A3A3] mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {when}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {r.max_players} players</span>
                    <span>·</span>
                    <span>{r.duration_min}m</span>
                    {r.price_total != null && (<><span>·</span><span className="inline-flex items-center gap-1"><PoundSterling className="w-3 h-3" /> £{r.price_total}</span></>)}
                  </div>
                </div>
                <div className="shrink-0 text-xs label-mono text-[#A3A3A3]">Open →</div>
              </Link>
            );
          })}
      </div>
    </div>
  );
}

export default GroupRequestsList;
